import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { SystemActionsContext } from './systemActions.context';

import {
  DateFilter,
  SelectFilter,
  Table,
  TableCell,
  TableRow,
  WithSpinner,
} from '../../../components';
import {
  buildTableFilterBody,
  formatLocaleDate,
  isFilterActive,
} from '../../../utils';

import { config } from '../../../api/config';
import { actions } from '../../../state';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const STATUS_LABELS = {
  STARTED: 'Vykdoma',
  SUCCESS: 'Sėkminga',
  FAILED: 'Nepavyko',
  'PART FAILED': 'Dalinai nepavyko',
};

const mapStatus = status => STATUS_LABELS[status] || status;

const formatOutputData = outputData => {
  if (outputData === null || outputData === undefined) {
    return '-';
  }
  if (typeof outputData === 'string') {
    return outputData || '-';
  }
  try {
    return JSON.stringify(outputData);
  } catch {
    return '-';
  }
};

const toNumberHeader = (values = []) =>
  values
    .filter(value => value !== null && value !== undefined)
    .map(value => ({ name: String(value), value }));

const toStringHeader = (values = []) =>
  values
    .filter(value => value !== null && value !== undefined)
    .map(value => ({ name: String(value), value: String(value) }));

const SystemActionsPage = ({
  dispatch,
  scheduledActionLogs: { loading, total, scheduledActionLogs, header = {} },
}) => {
  const {
    page,
    setPage,
    filterBody,
    setFilterBody,
    idFilter,
    setIdFilter,
    actionNameFilter,
    setActionNameFilter,
    categoryFilter,
    setCategoryFilter,
    statusFilter,
    setStatusFilter,
    startedAtLtFilter,
    setStartedAtLtFilter,
    startedAtGtFilter,
    setStartedAtGtFilter,
    endedAtLtFilter,
    setEndedAtLtFilter,
    endedAtGtFilter,
    setEndedAtGtFilter,
    durationMsFilter,
    setDurationMsFilter,
    sortOrder,
    setSortOrder,
    clearFilters,
  } = useContext(SystemActionsContext);

  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const clearScheduledActionLogs = useCallback(() => {
    dispatch(actions.monitoring.clearScheduledActionLogsAction());
  }, [dispatch]);

  const fetchScheduledActionLogs = useCallback(
    data => {
      dispatch(actions.monitoring.getScheduledActionLogsAction(data));
    },
    [dispatch]
  );

  useEffect(
    () => () => {
      clearScheduledActionLogs();
      clearFilters();
    },
    [clearScheduledActionLogs]
  );

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    fetchScheduledActionLogs({ ...(filterBody || {}), offset });
  }, [fetchScheduledActionLogs, filterBody, page]);

  const handleSubmit = useCallback(
    submitOptions => {
      const generatedFilter = [];

      if (idFilter.length) {
        generatedFilter.push({ field: 'id', value: idFilter });
      }
      if (actionNameFilter.length) {
        generatedFilter.push({
          field: 'action_name',
          value: actionNameFilter,
        });
      }
      if (categoryFilter.length) {
        generatedFilter.push({ field: 'category', value: categoryFilter });
      }
      if (statusFilter.length) {
        generatedFilter.push({ field: 'status', value: statusFilter });
      }
      if (startedAtGtFilter) {
        generatedFilter.push({
          field: 'started_at',
          expression: 'gte',
          value: startedAtGtFilter,
          type: 'timestamp',
        });
      }
      if (startedAtLtFilter) {
        generatedFilter.push({
          field: 'started_at',
          expression: 'lte',
          value: startedAtLtFilter,
          type: 'timestamp',
        });
      }
      if (endedAtGtFilter) {
        generatedFilter.push({
          field: 'ended_at',
          expression: 'gte',
          value: endedAtGtFilter,
          type: 'timestamp',
        });
      }
      if (endedAtLtFilter) {
        generatedFilter.push({
          field: 'ended_at',
          expression: 'lte',
          value: endedAtLtFilter,
          type: 'timestamp',
        });
      }
      if (durationMsFilter.length) {
        generatedFilter.push({
          field: 'duration_ms',
          value: durationMsFilter,
        });
      }

      setFilterBody(
        buildTableFilterBody({
          submitOptions,
          generatedFilter,
          sortOrder,
        })
      );
      setPage(1);
    },
    [
      actionNameFilter,
      categoryFilter,
      durationMsFilter,
      endedAtGtFilter,
      endedAtLtFilter,
      idFilter,
      setFilterBody,
      setPage,
      sortOrder,
      startedAtGtFilter,
      startedAtLtFilter,
      statusFilter,
    ]
  );

  const tableHeader = useMemo(() => {
    if (!header) {
      return [];
    }

    return [
      <SelectFilter
        title="ID"
        name="id"
        valueType="number"
        array={toNumberHeader(header.id)}
        filterState={idFilter}
        setFilterState={setIdFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', idFilter)}
      />,
      <SelectFilter
        title="Veiksmas"
        name="action_name"
        valueType="string"
        array={toStringHeader(header.action_name)}
        filterState={actionNameFilter}
        setFilterState={setActionNameFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', actionNameFilter)}
      />,
      <SelectFilter
        title="Kategorija"
        name="category"
        valueType="string"
        array={toStringHeader(header.category)}
        filterState={categoryFilter}
        setFilterState={setCategoryFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', categoryFilter)}
      />,
      <SelectFilter
        title="Būsena"
        name="status"
        valueType="string"
        array={(header.status || []).map(status => ({
          name: mapStatus(status),
          value: status,
        }))}
        filterState={statusFilter}
        setFilterState={setStatusFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', statusFilter)}
      />,
      <DateFilter
        title="Pradžia"
        name="started_at"
        lt={startedAtLtFilter}
        setLt={setStartedAtLtFilter}
        gt={startedAtGtFilter}
        setGt={setStartedAtGtFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('date', startedAtLtFilter, startedAtGtFilter)}
        titleWidth={'19rem'}
      />,
      <DateFilter
        title="Pabaiga"
        name="ended_at"
        lt={endedAtLtFilter}
        setLt={setEndedAtLtFilter}
        gt={endedAtGtFilter}
        setGt={setEndedAtGtFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('date', endedAtLtFilter, endedAtGtFilter)}
        titleWidth={'19rem'}
      />,
      <SelectFilter
        title="Trukmė (ms)"
        name="duration_ms"
        valueType="number"
        array={toNumberHeader(header.duration_ms)}
        filterState={durationMsFilter}
        setFilterState={setDurationMsFilter}
        onSubmit={handleSubmit}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isActive={isFilterActive('select', durationMsFilter)}
      />,
    ];
  }, [
    actionNameFilter,
    categoryFilter,
    durationMsFilter,
    endedAtGtFilter,
    endedAtLtFilter,
    handleSubmit,
    header,
    idFilter,
    setActionNameFilter,
    setCategoryFilter,
    setDurationMsFilter,
    setEndedAtGtFilter,
    setEndedAtLtFilter,
    setIdFilter,
    setSortOrder,
    setStartedAtGtFilter,
    setStartedAtLtFilter,
    setStatusFilter,
    sortOrder,
    startedAtGtFilter,
    startedAtLtFilter,
    statusFilter,
  ]);

  const renderTable = () =>
    scheduledActionLogs.map(
      ({
        id,
        action_name,
        category,
        status,
        started_at,
        ended_at,
        duration_ms,
        output_data,
        error_log,
      }) => (
        <TableRow key={id}>
          <TableCell align="center">{id}</TableCell>
          <TableCell>{action_name || '-'}</TableCell>
          <TableCell>{category || '-'}</TableCell>
          <TableCell>{mapStatus(status)}</TableCell>
          <TableCell>
            {started_at ? formatLocaleDate(started_at) : '-'}
          </TableCell>
          <TableCell>{ended_at ? formatLocaleDate(ended_at) : '-'}</TableCell>
          <TableCell align="center">{duration_ms ?? '-'}</TableCell>
          <TableCell>{formatOutputData(output_data)}</TableCell>
          <TableCell>{error_log || '-'}</TableCell>
        </TableRow>
      )
    );

  return (
    <WithSpinner loading={loading}>
      <Table
        header={tableHeader}
        isEmpty={!scheduledActionLogs.length}
        filter={true}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </WithSpinner>
  );
};

const mapStateToProps = state => ({
  scheduledActionLogs: state.monitoring.scheduledActionLogs,
});

SystemActionsPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  scheduledActionLogs: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    scheduledActionLogs: PropTypes.array.isRequired,
    header: PropTypes.object,
  }).isRequired,
};

export default connect(mapStateToProps)(SystemActionsPage);
